import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';
import type {
  translator,
  ParamsAndReturnTypes,
} from 'src/components/translate';
import { AppContext } from 'src/components/app-context';
import { getSortedQuery } from '@prisjakt/product-collection-urls';
import { getPropertyFromSortKey } from '../../utils/properties';
import {
  Dropdown,
  DropdownContent,
  DropdownToggle,
} from '@prisjakt/ui/dropdown';
import { ButtonType } from '@prisjakt/ui/button';
import { DropdownItem } from 'src/components/dropdown-item';

import * as S from './styles';
import { zindex } from 'src/ui/zindex';

export type SortQueryParams = {
  offset: number;
  direction: number;
  search: string;
};

type Props = {
  availableSorts: string[];
  availableSortsTranslations?: Record<string, string>;
  currentSort: string;
  onClick?: (sort: string) => void;
  className?: string;
};

export const getTranslationForSortOption = (
  sortKey: string,
  availableTranslations: Record<string, string>,
  t: ReturnType<typeof translator>
): string => {
  const property = getPropertyFromSortKey(sortKey);
  return availableTranslations[property]
    ? availableTranslations[property]
    : t(
        'common',
        `sort.${
          availableTranslations[sortKey]
            ? availableTranslations[sortKey]
            : sortKey
        }` as keyof ParamsAndReturnTypes['common']
      );
};

const SortOptions = ({
  availableSorts,
  availableSortsTranslations = {},
  currentSort,
  onClick,
  className,
}: Props) => {
  const { t } = useContext(AppContext);
  const location = useLocation();
  const sortOptions = Array.isArray(availableSorts) ? availableSorts : [];
  const currentSortName = getTranslationForSortOption(
    currentSort,
    availableSortsTranslations,
    t
  );
  if (!sortOptions.length) {
    return null;
  }

  return (
    <S.SortOptions className={className}>
      <S.SortOptionsTitle>{t('common', 'sort.title')}:</S.SortOptionsTitle>

      <Dropdown
        closeOnItemClick
        containerProps={{ 'data-test': 'SortOrderDropdown' }}
      >
        <DropdownToggle
          type={ButtonType.TEXT}
          showActiveIcon
          containerProps={{ 'data-test': 'DropdownToggle' }}
        >
          {currentSortName}
        </DropdownToggle>

        <DropdownContent zIndex={zindex.overlay}>
          {sortOptions
            .filter(sortOption => sortOption !== currentSort)
            .map(sort => {
              const queryParams = getSortedQuery({
                ...queryString.parse(location.search.replace(/^\?/, '')),
                offset: undefined,
                direction: undefined,
                sort,
              }) as SortQueryParams;

              return (
                <DropdownItem
                  key={sort}
                  to={`${location.pathname}?${queryString.stringify(
                    queryParams
                  )}`}
                  onClick={() => (onClick ? onClick(sort) : null)}
                  data-test="DropdownOption"
                  containerProps={{
                    'data-test-key': sort,
                  }}
                >
                  {getTranslationForSortOption(
                    sort,
                    availableSortsTranslations,
                    t
                  )}
                </DropdownItem>
              );
            })}
        </DropdownContent>
      </Dropdown>
    </S.SortOptions>
  );
};

export default SortOptions;
